import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Input } from '@/components/ui/input';
import { usePositions, useDepartments, useSeniorityLevels, useDivisions, useEmploymentTypes, positionsKeys } from '@/hooks/use-organizations';
import { updateEmployee } from '@/lib/employees';
import { employeesKeys } from '@/hooks/use-employees';
import type { EmployeeDetail } from '@/types/employee';
import { Loader2 } from 'lucide-react';

interface EditCurrentPositionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: EmployeeDetail;
}

export function EditCurrentPositionDialog({ open, onOpenChange, employee }: EditCurrentPositionDialogProps) {
  const queryClient = useQueryClient();
  const { data: positions = [], isLoading: positionsLoading } = usePositions();
  const { data: departments = [], isLoading: departmentsLoading } = useDepartments();
  const { data: seniorityLevels = [], isLoading: seniorityLevelsLoading } = useSeniorityLevels();
  const { data: divisions = [], isLoading: divisionsLoading } = useDivisions();
  const { data: employmentTypes = [], isLoading: employmentTypesLoading } = useEmploymentTypes();

  const [positionId, setPositionId] = useState<string>(employee.positionId || '');
  const [departmentId, setDepartmentId] = useState<string>(employee.departmentId || '');
  const [divisionId, setDivisionId] = useState<string>(employee.divisionId || '');
  const [employmentTypeId, setEmploymentTypeId] = useState<string>(employee.employmentTypeId || '');
  const [seniorityLevelId, setSeniorityLevelId] = useState<string>(employee.seniorityLevelId || '');
  const [startDate, setStartDate] = useState<string>(
    employee.startDate ? new Date(employee.startDate).toISOString().split('T')[0] : ''
  );

  useEffect(() => {
    if (open) {
      setPositionId(employee.positionId || '');
      setDepartmentId(employee.departmentId || '');
      setDivisionId(employee.divisionId || '');
      setEmploymentTypeId(employee.employmentTypeId || '');
      setSeniorityLevelId(employee.seniorityLevelId || '');
      setStartDate(
        employee.startDate ? new Date(employee.startDate).toISOString().split('T')[0] : ''
      );
    }
  }, [
    open,
    employee.positionId,
    employee.departmentId,
    employee.divisionId,
    employee.employmentTypeId,
    employee.seniorityLevelId,
    employee.startDate,
  ]);

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateEmployee>[1]) =>
      updateEmployee(employee.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });
      // Position ↔ employee assignment changed → refresh the Settings → Positions
      // employee counts (positionsKeys), else that list is stale until a reload.
      queryClient.invalidateQueries({ queryKey: positionsKeys.all });
      onOpenChange(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      positionId: positionId || null,
      departmentId: departmentId || null,
      divisionId: divisionId || null,
      employmentTypeId: employmentTypeId || undefined,
      seniorityLevelId: seniorityLevelId || null,
      startDate: startDate || undefined,
    });
  };

  const isLoading = positionsLoading || departmentsLoading || seniorityLevelsLoading || divisionsLoading || employmentTypesLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Current Position</DialogTitle>
          <DialogDescription>
            Update the employee's position, department, division, employment type, seniority level, and start date.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Position</Label>
              <SearchableSelect
                value={positionId}
                onChange={setPositionId}
                options={positions.map((p) => ({ id: p.id, name: p.title }))}
                placeholder="Select position"
                emptyText="No results found"
                disabled={isLoading || updateMutation.isPending}
                modal
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Select
                value={departmentId || '__none__'}
                onValueChange={(value) => setDepartmentId(value === '__none__' ? '' : value)}
                disabled={isLoading || updateMutation.isPending}
              >
                <SelectTrigger id="department">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {departments.map((department) => (
                    <SelectItem key={department.id} value={department.id}>
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="division">Division</Label>
              <Select
                value={divisionId || '__none__'}
                onValueChange={(value) => setDivisionId(value === '__none__' ? '' : value)}
                disabled={isLoading || updateMutation.isPending}
              >
                <SelectTrigger id="division">
                  <SelectValue placeholder="Select division" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {divisions.map((division) => (
                    <SelectItem key={division.id} value={division.id}>
                      {division.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employmentType">Employment Type</Label>
              <Select
                value={employmentTypeId || '__none__'}
                onValueChange={(value) => setEmploymentTypeId(value === '__none__' ? '' : value)}
                disabled={isLoading || updateMutation.isPending}
              >
                <SelectTrigger id="employmentType">
                  <SelectValue placeholder="Select employment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {employmentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="seniorityLevel">Seniority Level</Label>
              <Select
                value={seniorityLevelId || '__none__'}
                onValueChange={(value) => setSeniorityLevelId(value === '__none__' ? '' : value)}
                disabled={seniorityLevelsLoading || updateMutation.isPending}
              >
                <SelectTrigger id="seniorityLevel">
                  <SelectValue placeholder="Select seniority level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {seniorityLevels.map((level) => (
                    <SelectItem key={level.id} value={level.id}>
                      {level.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={updateMutation.isPending}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
